import React, { useState, useCallback } from 'react';
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ScatterChart,
  Scatter,
  Treemap,
  RadialBarChart,
  RadialBar,
  Sector,
} from 'recharts';
import type {
  ChartColorPaletteId,
  ChartLegendMode,
  ChartLegendPlacement,
  DashboardTabDefinition,
  DashboardVizKind,
  StandaloneChartConfig,
  WidgetDataBinding,
} from './types';
import CalendarHeatmapPanel from './CalendarHeatmapPanel';
import { CHART_COLOR_PALETTES } from './chartColorPalettes';
import { bodyLooksLikeHtml, normalizeWidgetHtmlBody } from './richTextBody';
import { useWebMapContext, type CrossFilter } from '../webMap/WebMapContext';
import { chartAxisLabelLayout, stackAxisLabel } from './chartAxisLabelUtils';

export type DashboardChartDisplayOptions = Pick<
  StandaloneChartConfig,
  | 'donutInnerRadiusPercent'
  | 'donutStartAngle'
  | 'legendMode'
  | 'legendPlacement'
  | 'chartLabelFontSize'
  | 'chartTextColor'
  | 'chartAxisLabelMode'
  | 'chartColorPaletteId'
  | 'heatmapCellSizePx'
>;

function paletteColors(id: ChartColorPaletteId | undefined): string[] {
  return CHART_COLOR_PALETTES[id ?? 'default'] ?? CHART_COLOR_PALETTES.default;
}

const tooltipStyle = {
  contentStyle: {
    background: 'rgba(20,20,30,0.92)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    backdropFilter: 'blur(8px)',
    fontSize: 12,
    color: '#e0e0e0',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  },
  itemStyle: { color: '#e0e0e0' },
  labelStyle: { color: '#aaa', fontWeight: 600, marginBottom: 2 },
};

const StackedAxisTick: React.FC<{
  x?: number;
  y?: number;
  payload?: { value?: unknown };
  fill: string;
  fontSize: number;
}> = ({ x = 0, y = 0, payload, fill, fontSize }) => {
  const lines = stackAxisLabel(payload?.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text fill={fill} fontSize={fontSize} textAnchor="middle">
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 14 : fontSize + 2}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

function EmptyViz({ message }: { message: string }) {
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 160,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        px: 2,
      }}
    >
      <Typography variant="body2" align="center">
        {message}
      </Typography>
    </Box>
  );
}

interface SingleVizProps {
  viz: DashboardVizKind;
  chartTitle?: string;
  textBody?: string;
  heatmapWeeks?: number;
  compact?: boolean;
  chartDisplay?: DashboardChartDisplayOptions;
  dataBinding?: WidgetDataBinding;
  scatterXField?: string;
  scatterYField?: string;
}

export function useWebMapGroupedData(
  dataBinding: WidgetDataBinding | undefined
): { name: string; value: number }[] | null {
  const ctx = useWebMapContext();
  if (!dataBinding?.boundLayerId || !dataBinding?.groupByField || !ctx) return null;
  const features = ctx.getFeaturesForLayer(dataBinding.boundLayerId);
  const groupField = dataBinding.groupByField;
  const valueField = dataBinding.valueField;
  const counts = new Map<string, number>();
  for (const f of features) {
    const key = String(f.properties?.[groupField] ?? 'Unknown');
    if (valueField) {
      const v = Number(f.properties?.[valueField]) || 0;
      counts.set(key, (counts.get(key) ?? 0) + v);
    } else {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function useCrossFilterClick(dataBinding?: WidgetDataBinding) {
  const ctx = useWebMapContext();
  const groupField = dataBinding?.groupByField;

  const handleClick = useCallback(
    (name: string) => {
      if (!ctx || !groupField) return;
      const filterKey = `chart_${groupField}`;
      const existing = ctx.crossFilters.get(filterKey);
      if (existing && existing.value === name) {
        ctx.removeCrossFilter(filterKey);
      } else {
        const cf: CrossFilter = { field: groupField, value: name, source: 'chart' };
        ctx.setCrossFilter(filterKey, cf);
      }
    },
    [ctx, groupField]
  );

  const activeValue = ctx && groupField
    ? ctx.crossFilters.get(`chart_${groupField}`)?.value
    : undefined;

  return { handleClick, activeValue };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderActiveDonutShape = (props: any) => {
  const {
    cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
  } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 2}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={1}
        style={{ filter: `drop-shadow(0 0 10px ${fill})` }}
      />
    </g>
  );
};

export const DashboardSingleViz: React.FC<SingleVizProps> = ({
  viz,
  chartTitle,
  textBody,
  heatmapWeeks = 6,
  compact,
  chartDisplay,
  dataBinding,
  scatterXField,
  scatterYField,
}) => {
  const theme = useTheme();
  const webMap = useWebMapContext();
  const grouped = useWebMapGroupedData(dataBinding);
  const isBound = grouped !== null;
  const weeks = Math.min(12, Math.max(4, heatmapWeeks ?? 6));
  const [activeDonutIdx, setActiveDonutIdx] = useState(0);
  const { handleClick: handleCrossClick, activeValue: crossActive } = useCrossFilterClick(dataBinding);

  const h = compact ? 200 : 260;
  const colors = paletteColors(chartDisplay?.chartColorPaletteId);
  const legendMode: ChartLegendMode = chartDisplay?.legendMode ?? 'percent';
  const legendPlacement: ChartLegendPlacement = chartDisplay?.legendPlacement ?? 'side';
  const labelFs = chartDisplay?.chartLabelFontSize ?? 11;
  const chartTextColor =
    chartDisplay?.chartTextColor?.trim() || theme.palette.text.secondary;
  const axisLabelMode = chartDisplay?.chartAxisLabelMode ?? 'auto';
  const barAxisLayout = chartAxisLabelLayout(axisLabelMode, true);
  const lineAxisLayout = chartAxisLabelLayout(axisLabelMode, false);
  const innerPct = Math.min(85, Math.max(0, chartDisplay?.donutInnerRadiusPercent ?? 55));
  const startAngle = chartDisplay?.donutStartAngle ?? 90;

  const onDonutEnter = useCallback((_: unknown, index: number) => setActiveDonutIdx(index), []);

  if (viz === 'text') {
    const raw = textBody || 'Add text in widget settings.';
    const html = normalizeWidgetHtmlBody(raw);
    const asHtml = bodyLooksLikeHtml(raw);
    return (
      <Box sx={{ p: 1.5, height: '100%', overflow: 'auto', color: chartTextColor, fontSize: labelFs }}>
        {asHtml ? (
          <Box
            sx={{
              '& img': { maxWidth: '100%', height: 'auto' },
              '& a': { color: 'primary.main' },
              '& p': { margin: '0.4em 0' },
              '& h1,& h2,& h3': { mt: 1, mb: 0.5 },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {html}
          </Typography>
        )}
      </Box>
    );
  }

  if (!grouped) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No data source configured
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Open widget settings and bind this widget to a Web Map layer to visualize data.
        </Typography>
      </Box>
    );
  }

  if (isBound && !grouped.length) {
    return <EmptyViz message="No features in the bound map layer — add data or adjust the map extent." />;
  }

  if (viz === 'bar') {
    const data = grouped.slice(0, 12);
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5}>
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: barAxisLayout.bottomMargin }}>
            <defs>
              {colors.map((c, i) => (
                <linearGradient key={i} id={`barGrad${i}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.6} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="name"
              angle={barAxisLayout.angle}
              textAnchor={barAxisLayout.textAnchor}
              height={barAxisLayout.height}
              interval={0}
              tick={
                barAxisLayout.stacked
                  ? <StackedAxisTick fill={chartTextColor} fontSize={labelFs - 1} />
                  : { fontSize: labelFs - 1, fill: chartTextColor }
              }
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: labelFs - 1, fill: chartTextColor }}
              allowDecimals={false}
              axisLine={false}
              tickLine={false}
            />
            <RechartsTooltip {...tooltipStyle} />
            <Bar
              dataKey="value"
              radius={[6, 6, 0, 0]}
              name="Count"
              animationDuration={800}
              cursor="pointer"
              onClick={(entry) => entry?.name && handleCrossClick(String(entry.name))}
            >
              {data.map((d, i) => (
                <Cell
                  key={i}
                  fill={`url(#barGrad${i % colors.length})`}
                  opacity={crossActive != null && crossActive !== d.name ? 0.3 : 1}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'line') {
    const data = grouped.slice(0, 20);
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5}>
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: lineAxisLayout.bottomMargin }}>
            <defs>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor={colors[0]} />
                <stop offset="100%" stopColor={colors[1] ?? colors[0]} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="name"
              angle={lineAxisLayout.angle}
              textAnchor={lineAxisLayout.textAnchor}
              height={lineAxisLayout.height}
              interval={0}
              tick={
                lineAxisLayout.stacked
                  ? <StackedAxisTick fill={chartTextColor} fontSize={labelFs - 1} />
                  : { fontSize: labelFs - 1, fill: chartTextColor }
              }
              axisLine={false}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: labelFs - 1, fill: chartTextColor }} allowDecimals={false} axisLine={false} tickLine={false} />
            <RechartsTooltip {...tooltipStyle} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="url(#lineGrad)"
              strokeWidth={2.5}
              dot={{ r: 3, fill: colors[0], strokeWidth: 0 }}
              activeDot={{ r: 5, fill: colors[0], stroke: '#fff', strokeWidth: 2 }}
              name="Count"
              animationDuration={800}
            />
          </LineChart>
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'area') {
    const data = grouped.slice(0, 20);
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5}>
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: lineAxisLayout.bottomMargin }}>
            <defs>
              <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors[0]} stopOpacity={0.5} />
                <stop offset="100%" stopColor={colors[0]} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="name"
              angle={lineAxisLayout.angle}
              textAnchor={lineAxisLayout.textAnchor}
              height={lineAxisLayout.height}
              interval={0}
              tick={
                lineAxisLayout.stacked
                  ? <StackedAxisTick fill={chartTextColor} fontSize={labelFs - 1} />
                  : { fontSize: labelFs - 1, fill: chartTextColor }
              }
              axisLine={false}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: labelFs - 1, fill: chartTextColor }} allowDecimals={false} axisLine={false} tickLine={false} />
            <RechartsTooltip {...tooltipStyle} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={colors[0]}
              strokeWidth={2}
              fill="url(#areaFill)"
              dot={{ r: 3, fill: colors[0], strokeWidth: 0 }}
              activeDot={{ r: 5, fill: colors[0], stroke: '#fff', strokeWidth: 2 }}
              name="Count"
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'donut') {
    const data = grouped.slice(0, 8);
    const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
    const outerR = compact ? 70 : 88;
    const innerR = Math.round((outerR * innerPct) / 100);
    const legendFormatter = (value: string, entry: { payload?: { value?: number } }) => {
      const v = entry?.payload?.value ?? data.find((d) => d.name === value)?.value ?? 0;
      if (legendMode === 'hidden') return '';
      if (legendMode === 'value') return `${value}: ${v}`;
      const pct = total > 0 ? ((Number(v) / total) * 100).toFixed(1) : '0';
      return `${value} ${pct}%`;
    };
    const legendCommon = {
      wrapperStyle: { fontSize: labelFs, color: chartTextColor },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: legendFormatter as any,
    };
    const legendVertical = legendPlacement === 'side';
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5} alignItems="center">
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 4, right: legendVertical ? 8 : 4, bottom: 4, left: 4 }}>
            <defs>
              {colors.map((c, i) => (
                <linearGradient key={i} id={`donutGrad${i}`} x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor={c} stopOpacity={1} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx={legendVertical ? '42%' : '50%'}
              cy="50%"
              startAngle={startAngle}
              endAngle={startAngle + 360}
              innerRadius={innerR}
              outerRadius={outerR}
              paddingAngle={3}
              activeIndex={activeDonutIdx}
              activeShape={renderActiveDonutShape}
              onMouseEnter={onDonutEnter}
              onClick={(_, idx) => {
                const d = data[idx];
                if (d) handleCrossClick(d.name);
              }}
              cursor="pointer"
              animationDuration={800}
              animationBegin={0}
              stroke="none"
            >
              {data.map((d, i) => (
                <Cell
                  key={i}
                  fill={`url(#donutGrad${i % colors.length})`}
                  opacity={crossActive != null && crossActive !== d.name ? 0.3 : 1}
                />
              ))}
            </Pie>
            <RechartsTooltip {...tooltipStyle} />
            {legendMode !== 'hidden' && (
              <Legend
                {...legendCommon}
                layout={legendVertical ? 'vertical' : 'horizontal'}
                verticalAlign={legendVertical ? 'middle' : 'bottom'}
                align={legendVertical ? 'right' : 'center'}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'radar') {
    const data = grouped.slice(0, 8);
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5} alignItems="center">
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
            <defs>
              <linearGradient id="radarFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors[0]} stopOpacity={0.5} />
                <stop offset="100%" stopColor={colors[1] ?? colors[0]} stopOpacity={0.15} />
              </linearGradient>
            </defs>
            <PolarGrid stroke="rgba(255,255,255,0.12)" />
            <PolarAngleAxis
              dataKey="name"
              tick={{ fontSize: labelFs, fill: chartTextColor }}
            />
            <PolarRadiusAxis tick={{ fontSize: labelFs - 2, fill: chartTextColor }} axisLine={false} />
            <Radar
              dataKey="value"
              stroke={colors[0]}
              strokeWidth={2}
              fill="url(#radarFill)"
              dot={{ r: 3, fill: colors[0], strokeWidth: 0 }}
              animationDuration={800}
            />
            <RechartsTooltip {...tooltipStyle} />
          </RadarChart>
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'scatter') {
    const features = dataBinding?.boundLayerId && webMap
      ? webMap.getFeaturesForLayer(dataBinding.boundLayerId)
      : [];
    const xField = scatterXField || dataBinding?.groupByField;
    const yField = scatterYField || dataBinding?.valueField;
    if (!xField || !yField) {
      return <EmptyViz message="Configure X and Y fields in widget settings to display scatter plot." />;
    }
    const scatterData = features
      .map((f) => ({
        x: Number(f.properties?.[xField]) || 0,
        y: Number(f.properties?.[yField]) || 0,
      }))
      .filter((d) => Number.isFinite(d.x) && Number.isFinite(d.y))
      .slice(0, 500);
    if (!scatterData.length) {
      return <EmptyViz message="No numeric data found for the selected fields." />;
    }
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5}>
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} stroke="rgba(255,255,255,0.08)" />
            <XAxis
              dataKey="x"
              type="number"
              name={xField}
              tick={{ fontSize: labelFs - 1, fill: chartTextColor }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              dataKey="y"
              type="number"
              name={yField}
              tick={{ fontSize: labelFs - 1, fill: chartTextColor }}
              axisLine={false}
              tickLine={false}
            />
            <RechartsTooltip {...tooltipStyle} cursor={{ strokeDasharray: '3 3' }} />
            <Scatter
              data={scatterData}
              fill={colors[0]}
              fillOpacity={0.7}
              animationDuration={600}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'treemap') {
    const data = grouped.slice(0, 20).map((d, i) => ({
      ...d,
      fill: colors[i % colors.length],
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const TreemapContent = (props: any) => {
      const { x, y, width, height, name, fill: cellFill } = props;
      if (width < 30 || height < 20) return null;
      const dimmed = crossActive != null && crossActive !== name;
      return (
        <g
          onClick={() => name && handleCrossClick(name)}
          style={{ cursor: 'pointer' }}
        >
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={4}
            ry={4}
            fill={cellFill}
            fillOpacity={dimmed ? 0.3 : 0.85}
            stroke="rgba(0,0,0,0.3)"
            strokeWidth={1.5}
          />
          {width > 50 && height > 30 && (
            <text
              x={x + width / 2}
              y={y + height / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#fff"
              fontSize={Math.min(12, width / 8)}
              fontWeight={600}
              opacity={dimmed ? 0.4 : 1}
            >
              {name?.length > 14 ? name.slice(0, 12) + '…' : name}
            </text>
          )}
        </g>
      );
    };
    return (
      <Stack sx={{ height: '100%', minHeight: h, p: 0.5 }} spacing={0.5}>
        {chartTitle && (
          <Typography variant="caption" sx={{ color: chartTextColor, fontSize: labelFs, fontWeight: 600, letterSpacing: 0.3 }}>
            {chartTitle}
          </Typography>
        )}
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={data}
            dataKey="value"
            nameKey="name"
            stroke="none"
            animationDuration={600}
            content={<TreemapContent />}
          />
        </ResponsiveContainer>
      </Stack>
    );
  }

  if (viz === 'calendar') {
    const calFeatures = dataBinding?.boundLayerId && webMap
      ? webMap.getFeaturesForLayer(dataBinding.boundLayerId)
      : [];
    return (
      <CalendarHeatmapPanel
        features={calFeatures}
        dateField={dataBinding?.groupByField}
        weeks={weeks}
        chartTitle={chartTitle}
        cellSizePx={chartDisplay?.heatmapCellSizePx}
      />
    );
  }

  return <EmptyViz message="Unknown visualization type." />;
};

/* ---------- Gauge Widget ---------- */

export const GaugeWidget: React.FC<{
  dataBinding?: WidgetDataBinding;
  gaugeMin?: number;
  gaugeMax?: number;
  gaugeLabel?: string;
  gaugeColor?: string;
  chartColorPaletteId?: ChartColorPaletteId;
}> = ({ dataBinding, gaugeMin = 0, gaugeMax = 100, gaugeLabel, gaugeColor, chartColorPaletteId }) => {
  const ctx = useWebMapContext();
  const colors = paletteColors(chartColorPaletteId);
  const fill = gaugeColor?.trim() || colors[0];

  let currentVal = 0;
  if (dataBinding?.boundLayerId && ctx) {
    const features = ctx.getFeaturesForLayer(dataBinding.boundLayerId);
    if (dataBinding.valueField) {
      const total = features.reduce(
        (s, f) => s + (Number(f.properties?.[dataBinding.valueField!]) || 0),
        0
      );
      currentVal = total;
    } else {
      currentVal = features.length;
    }
  }

  const pct = gaugeMax !== gaugeMin
    ? Math.min(100, Math.max(0, ((currentVal - gaugeMin) / (gaugeMax - gaugeMin)) * 100))
    : 0;

  const gaugeData = [
    { name: 'value', value: pct, fill },
    { name: 'empty', value: 100 - pct, fill: 'rgba(255,255,255,0.06)' },
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1 }}>
      <ResponsiveContainer width="100%" height="80%">
        <RadialBarChart
          innerRadius="60%"
          outerRadius="90%"
          data={[gaugeData[0]]}
          startAngle={210}
          endAngle={-30}
          barSize={14}
        >
          <RadialBar
            dataKey="value"
            cornerRadius={10}
            background={{ fill: 'rgba(255,255,255,0.06)' }}
            animationDuration={800}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <Box sx={{ textAlign: 'center', mt: -4 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: fill }}>
          {currentVal.toLocaleString()}
        </Typography>
        {gaugeLabel && (
          <Typography variant="caption" color="text.secondary">{gaugeLabel}</Typography>
        )}
      </Box>
    </Box>
  );
};

/* ---------- Tabbed Viz Widget ---------- */

export const VizTabsWidget: React.FC<{
  tabs: DashboardTabDefinition[];
  heatmapWeeks?: number;
  chartDisplay?: DashboardChartDisplayOptions;
  dataBinding?: WidgetDataBinding;
}> = ({ tabs, heatmapWeeks = 6, chartDisplay, dataBinding }) => {
  const [tab, setTab] = useState(0);
  const safeTabs = tabs.length
    ? tabs
    : [{ id: 'x', label: 'Empty', viz: 'text' as const, textBody: 'Add tabs in settings.' }];

  const current = safeTabs[Math.min(tab, safeTabs.length - 1)];

  return (
    <Stack sx={{ height: '100%', minHeight: 200, overflow: 'hidden' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 40, borderBottom: 1, borderColor: 'divider', px: 0.5 }}
      >
        {safeTabs.map((t, i) => (
          <Tab key={t.id} label={t.label} value={i} sx={{ minHeight: 40, py: 0, fontSize: '0.75rem' }} />
        ))}
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DashboardSingleViz
          viz={current.viz}
          textBody={current.textBody}
          compact
          heatmapWeeks={heatmapWeeks}
          chartDisplay={chartDisplay}
          dataBinding={dataBinding}
        />
      </Box>
    </Stack>
  );
};
